// Shared print-template picker for scan results.
//
// The Requested-Scan view opens this dialog before launching a print window.
// The dialog lets the user pick between the existing minimal template and
// the new branded template modelled after the Dekyi sample, and — when the
// branded template is chosen — collects the Verified-By / Approved-By
// signatories (typically the radiologist and consultant) that the design
// requires.
//
// Signatory fields are pre-filled from localStorage so users who print
// multiple reports in a session don't have to retype the same names every
// time. The "Performed by" entry is auto-filled from the logged-in user
// (the sonographer running the scan).

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, RadioGroup, FormControlLabel, Radio,
  TextField, Box, Typography, Divider, Stack, Alert,
} from '@mui/material';

import { generateScanReportHTML } from './ScanReportTemplate';
import { generateScanReportHTMLDekyi } from './ScanReportTemplateDekyi';

const SIG_STORAGE_KEY = 'scan.printSignatories.v1';

function readStoredSignatories() {
  try {
    const raw = localStorage.getItem(SIG_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}

function persistSignatories(sig) {
  try {
    localStorage.setItem(SIG_STORAGE_KEY, JSON.stringify({
      verifiedBy:    sig.verifiedBy    || '',
      verifiedTitle: sig.verifiedTitle || '',
      approvedBy:    sig.approvedBy    || '',
      approvedTitle: sig.approvedTitle || '',
      modality:      sig.modality      || '',
    }));
  } catch (_) { /* non-fatal */ }
}

export default function ScanPrintDialog({
  open,
  onClose,
  reportData,
  selectedPatient,
  userInfo,
  companyInfo,
  scanFormats = {},   // scan name → 'parameter' | 'narrative' (migration 040)
}) {
  // 'branded' is the default because it's the template that renders
  // narrative-marked scans descriptively (the standard template only
  // handles parameter tables). When the report includes any narrative
  // scan we keep this pre-selected + locked so the tech just hits
  // Print and gets the right layout.
  const [template, setTemplate] = useState('branded');

  // True when any scan in this report is marked narrative in the
  // catalog. Drives the pre-selection lock so the standard template
  // (which can't render narratives) doesn't get picked by accident.
  const hasNarrative = Object.values(scanFormats || {}).some((v) => v === 'narrative');

  // Whenever the dialog opens for a report containing a narrative
  // scan, snap the template back to Branded — the standard template
  // has no narrative branch.
  useEffect(() => {
    if (open && hasNarrative) setTemplate('branded');
  }, [open, hasNarrative]);
  const [sig, setSig] = useState({
    verifiedBy:    '',
    verifiedTitle: 'Radiologist',
    approvedBy:    '',
    approvedTitle: 'Senior Radiologist / Consultant',
    modality:      'Ultrasound',
    referringDoctor: '',
  });
  const [touched, setTouched] = useState(false);

  // Pre-fill signatories from the last successful print so users who run
  // batches of reports don't have to retype the same names every time.
  useEffect(() => {
    if (!open) return;
    const stored = readStoredSignatories();
    setSig((prev) => ({
      ...prev,
      verifiedBy:    stored.verifiedBy    || prev.verifiedBy,
      verifiedTitle: stored.verifiedTitle || prev.verifiedTitle,
      approvedBy:    stored.approvedBy    || prev.approvedBy,
      approvedTitle: stored.approvedTitle || prev.approvedTitle,
      modality:      stored.modality      || prev.modality,
    }));
    setTouched(false);
  }, [open]);

  const handleField = (key) => (e) => setSig((s) => ({ ...s, [key]: e.target.value }));

  const needsSignatories = template === 'branded';
  const missing = needsSignatories && (!sig.verifiedBy.trim() || !sig.approvedBy.trim());

  const handlePrint = () => {
    if (missing) { setTouched(true); return; }

    let html;
    if (template === 'branded') {
      persistSignatories(sig);
      html = generateScanReportHTMLDekyi(reportData, selectedPatient, userInfo, companyInfo, {
        verifiedBy:      sig.verifiedBy.trim(),
        verifiedTitle:   sig.verifiedTitle.trim(),
        approvedBy:      sig.approvedBy.trim(),
        approvedTitle:   sig.approvedTitle.trim(),
        modality:        sig.modality.trim(),
        referringDoctor: sig.referringDoctor.trim(),
      }, scanFormats);
    } else {
      html = generateScanReportHTML(reportData, selectedPatient, userInfo, companyInfo);
    }

    const w = window.open('', '_blank');
    if (!w) {
      // Popup blocked — surface a friendlier error than a silent failure.
      window.alert('Could not open the print window. Please allow pop-ups for this site and try again.');
      return;
    }
    w.document.write(html);
    w.document.close();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Print Scan Results</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Choose a report layout. The branded layout matches the printed Dekyi sample
          and requires the verifying radiologist and approving consultant before it can be printed.
        </Typography>

        <RadioGroup
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          sx={{ gap: 0.5 }}
        >
          <Box
            onClick={() => setTemplate('branded')}
            sx={{
              p: 1.25, border: '1.5px solid',
              borderColor: template === 'branded' ? 'primary.main' : 'divider',
              borderRadius: 1.5, cursor: 'pointer',
              bgcolor: template === 'branded' ? 'action.hover' : 'transparent',
            }}
          >
            <FormControlLabel
              value="branded"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
                    Branded (Dekyi layout)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Blue header band, scannable barcode, three-row signature block (Performed / Verified / Approved).
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box
            onClick={() => setTemplate('standard')}
            sx={{
              p: 1.25, border: '1.5px solid',
              borderColor: template === 'standard' ? 'primary.main' : 'divider',
              borderRadius: 1.5, cursor: 'pointer',
              bgcolor: template === 'standard' ? 'action.hover' : 'transparent',
            }}
          >
            <FormControlLabel
              value="standard"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
                    Standard
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The original minimal layout — no barcode or signature rows.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </RadioGroup>

        {template === 'branded' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1 }}>
              Signatories (required)
            </Typography>

            <Stack spacing={1.25}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField
                  label="Verified by — Name"
                  size="small"
                  value={sig.verifiedBy}
                  onChange={handleField('verifiedBy')}
                  error={touched && !sig.verifiedBy.trim()}
                  helperText={touched && !sig.verifiedBy.trim() ? 'Required' : ''}
                  required
                />
                <TextField
                  label="Verified by — Position"
                  size="small"
                  value={sig.verifiedTitle}
                  onChange={handleField('verifiedTitle')}
                  placeholder="e.g., Radiologist"
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField
                  label="Approved by — Name"
                  size="small"
                  value={sig.approvedBy}
                  onChange={handleField('approvedBy')}
                  error={touched && !sig.approvedBy.trim()}
                  helperText={touched && !sig.approvedBy.trim() ? 'Required' : ''}
                  required
                />
                <TextField
                  label="Approved by — Position"
                  size="small"
                  value={sig.approvedTitle}
                  onChange={handleField('approvedTitle')}
                  placeholder="e.g., Senior Radiologist / Consultant"
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField
                  label="Modality"
                  size="small"
                  value={sig.modality}
                  onChange={handleField('modality')}
                  placeholder="e.g., Ultrasound, X-Ray, CT"
                />
                <TextField
                  label="Referring Doctor"
                  size="small"
                  value={sig.referringDoctor}
                  onChange={handleField('referringDoctor')}
                  placeholder="Optional"
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                Performed by is auto-filled from your logged-in account
                (<strong>{userInfo?.name || userInfo?.username || '—'}</strong>).
              </Typography>

              {touched && missing && (
                <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>
                  Verified-by and Approved-by names are required for the branded layout.
                </Alert>
              )}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handlePrint}>Open Print Preview</Button>
      </DialogActions>
    </Dialog>
  );
}
